// src/slices/templatesSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  EncounterTemplate,
  EncounterTemplatePreference,
} from "../types/templates";
import { AppThunk } from "../store";
import { medicalsApi } from "../api/medicalsApi";
import templateApi from "../api/medicals/templateApi";

interface TemplatesState {
  soapTemplates: EncounterTemplate[];
  detailedTemplates: EncounterTemplate[];
  preferredSimpleTemplate: EncounterTemplatePreference | undefined;
  preferredDetailedTemplate: EncounterTemplatePreference | undefined;
}

const initialState: TemplatesState = {
  soapTemplates: [],
  detailedTemplates: [],
  preferredSimpleTemplate: undefined,
  preferredDetailedTemplate: undefined,
};

const templatesSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    setSoapTemplates(state, action: PayloadAction<EncounterTemplate[]>) {
      state.soapTemplates = action.payload;
    },
    setDetailedTemplates(state, action: PayloadAction<EncounterTemplate[]>) {
      state.detailedTemplates = action.payload;
    },
    setPreferredSimpleTemplate(
      state,
      action: PayloadAction<EncounterTemplatePreference | undefined>,
    ) {
      state.preferredSimpleTemplate = action.payload;
    },
    setPreferredDetailedTemplate(
      state,
      action: PayloadAction<EncounterTemplatePreference | undefined>,
    ) {
      state.preferredDetailedTemplate = action.payload;
    },
  },
});

export const {
  setSoapTemplates,
  setDetailedTemplates,
  setPreferredSimpleTemplate,
  setPreferredDetailedTemplate,
} = templatesSlice.actions;

export const getTemplates =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const [templates, templatePreferences] = await Promise.all([
        medicalsApi.getTemplates({
          type: "Encounter",
          limit: "0",
        }),
        templateApi.getTemplatePreferences({
          type: ["simple", "detailed"],
        }),
      ]);

      if (templates?.length > 0) {
        const detailed = templates.filter(
          (v) => v.definition.encounterType === "detailed",
        );
        const simple = templates.filter(
          (v) => v.definition.encounterType === "simple",
        );

        dispatch(setDetailedTemplates(detailed));
        dispatch(setSoapTemplates(simple));
      }

      if (templatePreferences?.length > 0) {
        const preferredSimple = templatePreferences.find(
          (v) => v.type === "simple",
        );
        const preferredDetailed = templatePreferences.find(
          (v) => v.type === "detailed",
        );

        dispatch(setPreferredSimpleTemplate(preferredSimple));
        dispatch(setPreferredDetailedTemplate(preferredDetailed));
      }
    } catch (e) {
      console.error("Error getting templates", e);
    }
  };
export default templatesSlice.reducer;
